import React, {useState} from "react"
import Recipients from "@irati-web/reward/src/components/Recipients"

import authentication from "@irati/auth"

import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {H2, Body} from "@irati/ui/src/Typography"
import GenericModal from "@irati/ui/src/GenericModal"
import {enumToArray} from "@irati/utils/enumToArray"
import history from "@irati/utils/history"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import {RecognitionInput, TeamAssignment, User} from "@irati/types"
import {Radio} from "@irati/ui/src/FormItem"

import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {DistributePayload, DistributeOnChange} from "../../../containers/AdminReward/DistributeReward"
import DistributeRewardStepIndicator from "./DistributeRewardStepIndicator"

export enum RewardValueTypes {
  Employees = "wallet.reward.admin.option.reward",
  Managers = "wallet.reward.admin.option.assign"
}

interface Props extends InjectIntl {
  assignReward: string,
  onAssignReward: (value: string) => void,
  payload: DistributePayload,
  onPayloadChange: DistributeOnChange,
  recognition: RecognitionInput,
  setRecognition: (recognition: RecognitionInput) => void
}

const DistributeRewardStep1: React.FC<Props> = ({
  assignReward,
  onPayloadChange,
  onAssignReward,
  payload,
  recognition,
  setRecognition,
  formatMessage
}) => {
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)

  const isAdmin = authentication.apps("wallet.flexiRewards.canTopUpRewardPoints")
  const assignRewardOptions = enumToArray(RewardValueTypes).map((option) => ({label: formatMessage({id: option}), value: option}))

  const handleRewardOption = (value: RewardValueTypes) => {
    if (value === RewardValueTypes.Managers) {
      tracking.selectAssignBudget()
    } else {
      tracking.selectRewardEmployees(isAdmin ? "Admin" : "Manager")
    }

    onPayloadChange({
      ...payload,
      destinationUsers: []
    })
    onAssignReward(value)
  }

  const toggleSuccessModal = (show: boolean) => setShowSuccessModal(show)

  const editManagers = () => {
    history.push("/admin/teams/view")
  }

  const handleRecipients = (beneficiary: User[]) => {
    if (assignReward === RewardValueTypes.Managers) {
      const managerOrLeader = beneficiary.filter((user) => {
        const isManagerOrLeader = user?.teamAssignments?.find((teamAssignment) => teamAssignment.assignment !== TeamAssignment.Basic)
        if (!isManagerOrLeader) {
          setShowSuccessModal(true)
        }
        return isManagerOrLeader
      })
      onPayloadChange({
        ...payload,
        destinationUsers: managerOrLeader
      })
    } else {
      onPayloadChange({
        ...payload,
        destinationUsers: beneficiary
      })
      setRecognition({...recognition, userIds: beneficiary})
    }
  }

  return (
    <>
      <DistributeRewardStepIndicator currentStep={1} />
      {isAdmin && (
        <Radio name="choose-reward-type" value={assignReward} onChange={handleRewardOption} options={assignRewardOptions} />
      )}
      <Recipients recipients={payload.destinationUsers} setRecipients={handleRecipients} sameBUOnly={false} isRequired />

      <GenericModal hideActions show={showSuccessModal} onHide={() => toggleSuccessModal(false)}>
        <H2 marginBottom={1}>
          <FormattedMessage id="wallet.reward.modal.error.heading.oops" />
        </H2>
        <Body>
          <FormattedMessage id="wallet.reward.modal.body.text" />
        </Body>
        <Flex marginTop={5}>
          <Button primary marginRight onClick={editManagers}>
            <FormattedMessage id="wallet.reward.modal.editManagers" />
          </Button>
          <Button onClick={() => toggleSuccessModal(false)}>
            <FormattedMessage id="wallet.reward.modal.maybeLater" />
          </Button>
        </Flex>
      </GenericModal>
    </>
  )
}

export default injectIntl(DistributeRewardStep1)
